import { useState, useEffect, useRef, useCallback } from 'react';
import { Loader2, Eye, Play, RefreshCw, AlertTriangle, FolderTree, PauseCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

const POLL_INTERVAL_MS = 4000;

export function PennylaneInvoiceFilesPanel() {
  const [previewPolling, setPreviewPolling] = useState(false);
  const [applyPolling, setApplyPolling] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [preview, setPreview] = useState(null);
  const [applyResult, setApplyResult] = useState(null);
  const [applyFailed, setApplyFailed] = useState(null);
  const [applyProgress, setApplyProgress] = useState(null);
  const [target, setTarget] = useState('missing');
  const [driveSettings, setDriveSettings] = useState(null);
  const [pauseStatus, setPauseStatus] = useState(null);
  const [pauseToggling, setPauseToggling] = useState(false);
  const pollRef = useRef(null);
  const elapsedRef = useRef(null);
  const startTimeRef = useRef(null);

  // Check-failed breakdown: read-only, independent of preview/apply above
  // -- its own poll refs so it can run concurrently without disturbing an
  // in-flight preview/apply poll (those three share one pollRef/elapsedRef
  // pair above; a fourth operation piggybacking on that shared pair would
  // silently stop whichever of preview/apply/breakdown started last, the
  // exact bug class DrivePathAuditPanel.jsx's useJobPolling comment warns
  // about -- so this stays fully separate instead).
  const [breakdownDispatching, setBreakdownDispatching] = useState(false);
  const [breakdownPolling, setBreakdownPolling] = useState(false);
  const [breakdownElapsed, setBreakdownElapsed] = useState(0);
  const [breakdown, setBreakdown] = useState(null);
  const [breakdownFailed, setBreakdownFailed] = useState(null);
  const breakdownPollRef = useRef(null);
  const breakdownElapsedRef = useRef(null);
  const breakdownStartTimeRef = useRef(null);

  // Ownership probe: read-only, independent of every operation above for
  // the same reason breakdown's refs are separate -- its own dispatch/poll
  // pair so a long-running probe (N credentials x 2 endpoints x every
  // check-failed row) can't be silently stopped by another operation's
  // poll teardown, and vice versa.
  const [probeDispatching, setProbeDispatching] = useState(false);
  const [probePolling, setProbePolling] = useState(false);
  const [probeElapsed, setProbeElapsed] = useState(0);
  const [probeProgress, setProbeProgress] = useState(null);
  const [probe, setProbe] = useState(null);
  const [probeFailed, setProbeFailed] = useState(null);
  const probePollRef = useRef(null);
  const probeElapsedRef = useRef(null);
  const probeStartTimeRef = useRef(null);

  // Reattribution preview/apply: read-only preview, write-on-apply. Its
  // own dispatch/poll state, independent of every operation above for the
  // same reason breakdown/probe's refs are separate.
  const [reattachDispatching, setReattachDispatching] = useState(false);
  const [reattachPreviewPolling, setReattachPreviewPolling] = useState(false);
  const [reattachApplyPolling, setReattachApplyPolling] = useState(false);
  const [reattachElapsed, setReattachElapsed] = useState(0);
  const [reattachPreview, setReattachPreview] = useState(null);
  const [reattachApplyResult, setReattachApplyResult] = useState(null);
  const [reattachApplyProgress, setReattachApplyProgress] = useState(null);
  const [reattachFailed, setReattachFailed] = useState(null);
  const reattachPollRef = useRef(null);
  const reattachElapsedRef = useRef(null);
  const reattachStartTimeRef = useRef(null);

  // Intercompany candidates: read-only, simple synchronous Mongo query --
  // no job/poll machinery needed.
  const [icDispatching, setIcDispatching] = useState(false);
  const [icResult, setIcResult] = useState(null);
  const [icFailed, setIcFailed] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.driveSettingsGet();
        setDriveSettings(data);
      } catch {
        // Non-fatal: the destination banner just won't render; Apply will
        // still surface the real error if the root turns out to be unset.
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.pennylaneInvoiceFilesPauseStatus();
        setPauseStatus(data);
      } catch {
        // Non-fatal: the toggle just won't render.
      }
    })();
  }, []);

  const togglePause = async () => {
    setPauseToggling(true);
    try {
      const { data } = await diagnosticsAPI.pennylaneInvoiceFilesSetPause(!pauseStatus?.paused);
      setPauseStatus(data);
      toast.success(data.paused ? 'Scheduled pull paused' : 'Scheduled pull resumed');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setPauseToggling(false);
    }
  };

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (elapsedRef.current) {
      clearInterval(elapsedRef.current);
      elapsedRef.current = null;
    }
    setPreviewPolling(false);
    setApplyPolling(false);
  }, []);

  useEffect(() => stopPolling, [stopPolling]);

  // On mount: recover a previous apply job so a page refresh doesn't lose
  // the result of a long-running backfill.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.pennylaneInvoiceFilesJobLatest('apply');
        const job = data?.job;
        if (!job) return;
        if (job.status === 'completed' && job.result) {
          setApplyResult(job.result);
        } else if (job.status === 'running') {
          startTimeRef.current = Date.now();
          setApplyProgress(job.progress || null);
          startPolling(job._id, 'apply');
        } else if (job.status === 'failed') {
          setApplyFailed(job.error);
          setApplyProgress(job.progress || null);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startPolling = useCallback((id, operation) => {
    stopPolling();
    if (operation === 'preview') setPreviewPolling(true);
    else setApplyPolling(true);

    startTimeRef.current = startTimeRef.current ?? Date.now();

    elapsedRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);

    pollRef.current = setInterval(async () => {
      try {
        const { data } = await diagnosticsAPI.pennylaneInvoiceFilesJobStatus(id);
        if (operation === 'apply' && data.progress) setApplyProgress(data.progress);
        if (data.status === 'completed') {
          stopPolling();
          startTimeRef.current = null;
          if (operation === 'preview') {
            setPreview(data.result);
            toast.success('Preview complete');
          } else {
            setApplyResult(data.result);
            setApplyFailed(null);
            toast.success(
              `Pull complete: ${data.result?.stored ?? 0} stored, ` +
              `${data.result?.not_available ?? 0} not available, ` +
              `${data.result?.failed ?? 0} failed`
            );
            setPreview(null);
          }
        } else if (data.status === 'failed') {
          stopPolling();
          startTimeRef.current = null;
          if (operation === 'apply') setApplyFailed(data.error);
          toast.error(data.error ?? `${operation} failed`);
        }
      } catch (err) {
        stopPolling();
        startTimeRef.current = null;
        toast.error(getErrorMessage(err));
      }
    }, POLL_INTERVAL_MS);
  }, [stopPolling]);

  const stopBreakdownPolling = useCallback(() => {
    if (breakdownPollRef.current) {
      clearInterval(breakdownPollRef.current);
      breakdownPollRef.current = null;
    }
    if (breakdownElapsedRef.current) {
      clearInterval(breakdownElapsedRef.current);
      breakdownElapsedRef.current = null;
    }
    breakdownStartTimeRef.current = null;
    setBreakdownPolling(false);
  }, []);

  useEffect(() => stopBreakdownPolling, [stopBreakdownPolling]);

  const startBreakdownPolling = useCallback((id) => {
    stopBreakdownPolling();
    setBreakdownPolling(true);
    setBreakdownElapsed(0);
    breakdownStartTimeRef.current = Date.now();

    breakdownElapsedRef.current = setInterval(() => {
      setBreakdownElapsed(Math.floor((Date.now() - breakdownStartTimeRef.current) / 1000));
    }, 1000);

    breakdownPollRef.current = setInterval(async () => {
      try {
        const { data } = await diagnosticsAPI.pennylaneInvoiceFilesJobStatus(id);
        if (data.status === 'completed') {
          stopBreakdownPolling();
          setBreakdown(data.result);
          setBreakdownFailed(null);
          toast.success(`Breakdown complete: ${data.result?.total_check_failed ?? 0} check-failed row(s)`);
        } else if (data.status === 'failed') {
          stopBreakdownPolling();
          setBreakdownFailed(data.error);
          toast.error(data.error ?? 'breakdown failed');
        }
      } catch (err) {
        stopBreakdownPolling();
        toast.error(getErrorMessage(err));
      }
    }, POLL_INTERVAL_MS);
  }, [stopBreakdownPolling]);

  // On mount: recover a previous breakdown job the same way apply's is
  // recovered above, independently.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.pennylaneInvoiceFilesJobLatest('check_failed_breakdown');
        const job = data?.job;
        if (!job) return;
        if (job.status === 'completed' && job.result) {
          setBreakdown(job.result);
        } else if (job.status === 'running') {
          startBreakdownPolling(job._id);
        } else if (job.status === 'failed') {
          setBreakdownFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runBreakdown = async () => {
    setBreakdownDispatching(true);
    setBreakdown(null);
    setBreakdownFailed(null);
    try {
      const { data } = await diagnosticsAPI.pennylaneCheckFailedBreakdownStart(target);
      startBreakdownPolling(data.job_id);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBreakdownDispatching(false);
    }
  };

  const stopProbePolling = useCallback(() => {
    if (probePollRef.current) {
      clearInterval(probePollRef.current);
      probePollRef.current = null;
    }
    if (probeElapsedRef.current) {
      clearInterval(probeElapsedRef.current);
      probeElapsedRef.current = null;
    }
    probeStartTimeRef.current = null;
    setProbePolling(false);
  }, []);

  useEffect(() => stopProbePolling, [stopProbePolling]);

  const startProbePolling = useCallback((id) => {
    stopProbePolling();
    setProbePolling(true);
    setProbeElapsed(0);
    probeStartTimeRef.current = Date.now();

    probeElapsedRef.current = setInterval(() => {
      setProbeElapsed(Math.floor((Date.now() - probeStartTimeRef.current) / 1000));
    }, 1000);

    probePollRef.current = setInterval(async () => {
      try {
        const { data } = await diagnosticsAPI.pennylaneInvoiceFilesJobStatus(id);
        if (data.progress) setProbeProgress(data.progress);
        if (data.status === 'completed') {
          stopProbePolling();
          setProbe(data.result);
          setProbeFailed(null);
          const owner = data.result?.summary?.single_owner_across_all_rows;
          toast.success(
            owner
              ? `Probe complete: all rows confirmed-owned by ${owner}`
              : `Probe complete: ${data.result?.total_check_failed_rows_probed ?? 0} row(s) checked, no single owner across all`
          );
        } else if (data.status === 'failed') {
          stopProbePolling();
          setProbeFailed(data.error);
          toast.error(data.error ?? 'ownership probe failed');
        }
      } catch (err) {
        stopProbePolling();
        toast.error(getErrorMessage(err));
      }
    }, POLL_INTERVAL_MS);
  }, [stopProbePolling]);

  // On mount: recover a previous probe job, independently.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.pennylaneInvoiceFilesJobLatest('ownership_probe');
        const job = data?.job;
        if (!job) return;
        if (job.status === 'completed' && job.result) {
          setProbe(job.result);
        } else if (job.status === 'running') {
          setProbeProgress(job.progress || null);
          startProbePolling(job._id);
        } else if (job.status === 'failed') {
          setProbeFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runProbe = async () => {
    setProbeDispatching(true);
    setProbe(null);
    setProbeFailed(null);
    setProbeProgress(null);
    try {
      const { data } = await diagnosticsAPI.pennylaneOwnershipProbeStart(target);
      startProbePolling(data.job_id);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setProbeDispatching(false);
    }
  };

  const stopReattachPolling = useCallback(() => {
    if (reattachPollRef.current) {
      clearInterval(reattachPollRef.current);
      reattachPollRef.current = null;
    }
    if (reattachElapsedRef.current) {
      clearInterval(reattachElapsedRef.current);
      reattachElapsedRef.current = null;
    }
    reattachStartTimeRef.current = null;
    setReattachPreviewPolling(false);
    setReattachApplyPolling(false);
  }, []);

  useEffect(() => stopReattachPolling, [stopReattachPolling]);

  const startReattachPolling = useCallback((id, operation) => {
    stopReattachPolling();
    if (operation === 'reattribution_preview') setReattachPreviewPolling(true);
    else setReattachApplyPolling(true);
    setReattachElapsed(0);
    reattachStartTimeRef.current = Date.now();

    reattachElapsedRef.current = setInterval(() => {
      setReattachElapsed(Math.floor((Date.now() - reattachStartTimeRef.current) / 1000));
    }, 1000);

    reattachPollRef.current = setInterval(async () => {
      try {
        const { data } = await diagnosticsAPI.pennylaneInvoiceFilesJobStatus(id);
        if (operation === 'reattribution_apply' && data.progress) setReattachApplyProgress(data.progress);
        if (data.status === 'completed') {
          stopReattachPolling();
          if (operation === 'reattribution_preview') {
            setReattachPreview(data.result);
            toast.success(`Preview complete: ${data.result?.reattributable_count ?? 0} row(s) reattributable`);
          } else {
            setReattachApplyResult(data.result);
            setReattachFailed(null);
            toast.success(`Apply complete: ${data.result?.reattributed_count ?? 0} row(s) reattributed`);
            setReattachPreview(null);
          }
        } else if (data.status === 'failed') {
          stopReattachPolling();
          if (operation === 'reattribution_apply') setReattachFailed(data.error);
          toast.error(data.error ?? `${operation} failed`);
        }
      } catch (err) {
        stopReattachPolling();
        toast.error(getErrorMessage(err));
      }
    }, POLL_INTERVAL_MS);
  }, [stopReattachPolling]);

  // On mount: recover previous reattribution-preview/apply jobs, independently.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.pennylaneInvoiceFilesJobLatest('reattribution_preview');
        const job = data?.job;
        if (!job) return;
        if (job.status === 'completed' && job.result) {
          setReattachPreview(job.result);
        } else if (job.status === 'running') {
          startReattachPolling(job._id, 'reattribution_preview');
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await diagnosticsAPI.pennylaneInvoiceFilesJobLatest('reattribution_apply');
        const job = data?.job;
        if (!job) return;
        if (job.status === 'completed' && job.result) {
          setReattachApplyResult(job.result);
        } else if (job.status === 'running') {
          setReattachApplyProgress(job.progress || null);
          startReattachPolling(job._id, 'reattribution_apply');
        } else if (job.status === 'failed') {
          setReattachFailed(job.error);
        }
      } catch {
        // Ignore initial status check failures.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runReattachPreview = async () => {
    setReattachDispatching(true);
    setReattachPreview(null);
    setReattachApplyResult(null);
    setReattachFailed(null);
    try {
      const { data } = await diagnosticsAPI.pennylaneReattributionPreviewStart(target);
      startReattachPolling(data.job_id, 'reattribution_preview');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setReattachDispatching(false);
    }
  };

  const runReattachApply = async () => {
    const count = reattachPreview ? reattachPreview.reattributable_count : null;
    if (!window.confirm(
      count != null
        ? `This will reattribute ${count} row(s) to their confirmed owner entity_id, re-confirming each fresh before writing. Continue?`
        : `This will re-derive the ownership probe fresh and reattribute every confirmed row (target: ${target}). Continue?`
    )) return;

    setReattachDispatching(true);
    setReattachApplyResult(null);
    setReattachFailed(null);
    setReattachApplyProgress(null);
    try {
      const { data } = await diagnosticsAPI.pennylaneReattributionApplyStart(target);
      startReattachPolling(data.job_id, 'reattribution_apply');
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setReattachDispatching(false);
    }
  };

  const runIntercompanyCandidates = async () => {
    setIcDispatching(true);
    setIcFailed(null);
    try {
      const { data } = await diagnosticsAPI.pennylaneIntercompanyCandidates();
      setIcResult(data);
    } catch (err) {
      setIcFailed(getErrorMessage(err));
    } finally {
      setIcDispatching(false);
    }
  };

  const runPreview = async () => {
    setDispatching(true);
    setPreview(null);
    setApplyResult(null);
    setApplyFailed(null);
    setApplyProgress(null);
    setElapsed(0);
    startTimeRef.current = Date.now();
    try {
      const { data } = await diagnosticsAPI.pennylaneInvoiceFilesPreviewStart(target);
      startPolling(data.job_id, 'preview');
    } catch (err) {
      startTimeRef.current = null;
      toast.error(getErrorMessage(err));
    } finally {
      setDispatching(false);
    }
  };

  const runApply = async () => {
    const count = preview ? preview.retrievable_count : null;
    const pauseWarning = pauseStatus?.paused
      ? '\n\nThe scheduled pull is currently PAUSED. Running Apply now overrides that pause for this one run only.'
      : '';
    if (!window.confirm(
      (count != null
        ? `This will pull ${count} PDF(s) from Pennylane and file them into Drive. Continue?`
        : `This will attempt to pull and file missing Pennylane invoice PDFs (target: ${target}). Continue?`
      ) + pauseWarning
    )) return;

    setDispatching(true);
    setApplyResult(null);
    setApplyFailed(null);
    setApplyProgress(null);
    setElapsed(0);
    startTimeRef.current = Date.now();
    try {
      const { data } = await diagnosticsAPI.pennylaneInvoiceFilesApplyStart(target);
      startPolling(data.job_id, 'apply');
    } catch (err) {
      startTimeRef.current = null;
      toast.error(getErrorMessage(err));
    } finally {
      setDispatching(false);
    }
  };

  const busy = dispatching || previewPolling || applyPolling;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Pennylane Invoice File Pull</CardTitle>
        <p className="text-xs text-muted-foreground mt-0.5">
          Pulls each Pennylane invoice's source PDF (GET .../customer_invoices or
          supplier_invoices {'->'} public_file_url) and files it into the same Drive tree
          the Gmail-ingestion pipeline uses, then stamps file_url. Preview live-checks
          Pennylane without writing; Apply re-checks fresh and pulls. Also runs
          automatically every 6 hours for newly-missing invoices.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* ── Pause control: scheduled 6-hourly pull only, manual Preview/Apply unaffected ── */}
        {pauseStatus && (
          <div className={`border rounded-lg p-2 flex items-center gap-3 ${pauseStatus.paused ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/20' : 'bg-muted/30'}`}>
            <Switch
              checked={pauseStatus.paused}
              onCheckedChange={togglePause}
              disabled={pauseToggling}
              aria-label="Pause scheduled Pennylane invoice-file pull"
            />
            <div className="min-w-0">
              <p className="text-xs font-medium flex items-center gap-1">
                {pauseStatus.paused && <PauseCircle className="h-3.5 w-3.5 text-amber-600" />}
                {pauseStatus.paused ? 'Scheduled pull paused' : 'Scheduled pull active (every 6 hours)'}
              </p>
              {pauseStatus.paused && (
                <p className="text-[11px] text-amber-700 dark:text-amber-400">
                  Paused by {pauseStatus.paused_by} at {pauseStatus.paused_at}
                </p>
              )}
            </div>
          </div>
        )}

        {/* ── Destination path: where these PDFs are about to land ── */}
        {driveSettings && (
          driveSettings.configured ? (
            <div className="border rounded-lg p-2 flex items-start gap-2 bg-muted/30">
              <FolderTree className="h-4 w-4 mt-0.5 flex-shrink-0 text-muted-foreground" />
              <div className="min-w-0">
                <p className="text-xs font-medium">Destination</p>
                <p className="text-xs text-muted-foreground font-mono break-all">
                  {driveSettings.drive_id ? (driveSettings.drive_name || driveSettings.drive_id) : 'My Drive'}
                  {' › '}{driveSettings.root_folder_name || driveSettings.root_folder_id}
                  {' › '}Jurisdiction {' › '}Entity{' › '}Year{' › '}Month{' › '}Invoices{' › '}Inbound/Outbound
                </p>
              </div>
            </div>
          ) : (
            <div className="border-2 rounded-lg p-2 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/20 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />
              <p className="text-xs text-amber-700 dark:text-amber-400">
                Drive destination root is not configured. Apply will fail until it's set under Integrations &gt; Email &amp; Drive.
              </p>
            </div>
          )
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <Button onClick={runPreview} disabled={busy} size="sm">
            {previewPolling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
            Preview
          </Button>

          <select
            value={target}
            onChange={(e) => { setTarget(e.target.value); setPreview(null); }}
            disabled={busy}
            className="h-9 rounded-md border border-input bg-background px-2 text-xs"
          >
            <option value="missing">All missing</option>
            <option value="retryable">Retry pending/failed only</option>
          </select>

          <Button
            onClick={runApply}
            disabled={busy || (driveSettings && !driveSettings.configured)}
            size="sm"
            variant={preview && preview.retrievable_count > 0 ? 'default' : 'outline'}
          >
            {applyPolling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
            {preview ? `Apply (${fmtInt(preview.retrievable_count)} retrievable)` : 'Apply'}
          </Button>

          {(previewPolling || applyPolling) && (
            <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-1">
              <RefreshCw className="h-3 w-3 animate-spin" />
              {elapsed}s
            </span>
          )}
        </div>

        {/* ── Live apply progress: n of total, running outcome counts, failures as they happen ── */}
        {applyPolling && applyProgress && (
          <div className="space-y-2">
            <Badge className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
              PULLING -- {fmtInt(applyProgress.processed)} of {fmtInt(applyProgress.total)} processed
            </Badge>
            <div className="grid grid-cols-3 gap-2">
              <div className="border rounded-lg p-2 text-center border-green-200 dark:border-green-800">
                <p className="text-xs text-muted-foreground">Stored</p>
                <p className="text-lg font-semibold tabular-nums text-green-600">{fmtInt(applyProgress.stored)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Not available</p>
                <p className="text-lg font-semibold tabular-nums text-muted-foreground">{fmtInt(applyProgress.not_available)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center border-red-200 dark:border-red-800">
                <p className="text-xs text-muted-foreground">Failed</p>
                <p className="text-lg font-semibold tabular-nums text-red-600">{fmtInt(applyProgress.failed)}</p>
              </div>
            </div>
            {applyProgress.failed_samples && applyProgress.failed_samples.length > 0 && (
              <details>
                <summary className="text-sm font-medium cursor-pointer text-red-600">
                  Failures so far ({applyProgress.failed_samples.length})
                </summary>
                <div className="mt-2 overflow-x-auto border rounded-lg max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <tbody>
                      {applyProgress.failed_samples.map((s, i) => (
                        <tr key={s.id || i} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono">{s.invoice_number}</td>
                          <td className="px-2 py-1">{s.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}
          </div>
        )}

        {/* ── Preview result ── */}
        {preview && (
          <div className="space-y-3">
            <Badge className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
              PREVIEW (read-only, target: {preview.target})
            </Badge>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Missing file</p>
                <p className="text-lg font-semibold tabular-nums">{fmtInt(preview.total_missing_file)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center border-green-200 dark:border-green-800">
                <p className="text-xs text-muted-foreground">Retrievable from Pennylane</p>
                <p className="text-lg font-semibold tabular-nums text-green-600">{fmtInt(preview.retrievable_count)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Genuinely absent</p>
                <p className="text-lg font-semibold tabular-nums text-muted-foreground">{fmtInt(preview.genuinely_absent_count)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center border-red-200 dark:border-red-800">
                <p className="text-xs text-muted-foreground">Check failed</p>
                <p className="text-lg font-semibold tabular-nums text-red-600">{fmtInt(preview.check_failed_count)}</p>
              </div>
            </div>

            {preview.check_failed_samples && preview.check_failed_samples.length > 0 && (
              <details>
                <summary className="text-sm font-medium cursor-pointer text-red-600">
                  Check-failed samples ({preview.check_failed_samples.length})
                </summary>
                <div className="mt-2 overflow-x-auto border rounded-lg max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <tbody>
                      {preview.check_failed_samples.map((s, i) => (
                        <tr key={s.id || i} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono">{s.invoice_number}</td>
                          <td className="px-2 py-1">{s.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            <p className="text-[10px] text-muted-foreground">Generated: {preview.generated_at}</p>
          </div>
        )}

        {/* ── Apply failed (systemic, e.g. Drive not connected) ── */}
        {applyFailed && (
          <div className="space-y-2">
            <Badge className="text-[10px] bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300">
              APPLY FAILED
            </Badge>
            <div className="border-2 rounded-lg p-3 border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/20 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" />
              <p className="text-xs text-red-600">{applyFailed}</p>
            </div>
            {applyProgress && (
              <p className="text-[10px] text-muted-foreground">
                Last known progress before failure: {fmtInt(applyProgress.processed)} of {fmtInt(applyProgress.total)} processed
                &middot; {fmtInt(applyProgress.stored)} stored &middot; {fmtInt(applyProgress.not_available)} not available
                &middot; {fmtInt(applyProgress.failed)} failed
              </p>
            )}
          </div>
        )}

        {/* ── Apply result ── */}
        {applyResult && (
          <div className="space-y-3">
            <Badge className="text-[10px] bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
              APPLIED ({applyResult.target})
            </Badge>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Attempted</p>
                <p className="text-lg font-semibold tabular-nums">{fmtInt(applyResult.total_attempted)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center border-green-200 dark:border-green-800">
                <p className="text-xs text-muted-foreground">Stored</p>
                <p className="text-lg font-semibold tabular-nums text-green-600">{fmtInt(applyResult.stored)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center">
                <p className="text-xs text-muted-foreground">Not available</p>
                <p className="text-lg font-semibold tabular-nums text-muted-foreground">{fmtInt(applyResult.not_available)}</p>
              </div>
              <div className="border rounded-lg p-2 text-center border-red-200 dark:border-red-800">
                <p className="text-xs text-muted-foreground">Failed</p>
                <p className="text-lg font-semibold tabular-nums text-red-600">{fmtInt(applyResult.failed)}</p>
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              {applyResult.elapsed_seconds}s elapsed &middot; {applyResult.throughput_per_minute ?? '-'} invoices/min
            </p>

            {applyResult.failed_samples && applyResult.failed_samples.length > 0 && (
              <details>
                <summary className="text-sm font-medium cursor-pointer text-red-600">
                  Failed samples ({applyResult.failed_samples.length})
                </summary>
                <div className="mt-2 overflow-x-auto border rounded-lg max-h-48 overflow-y-auto">
                  <table className="w-full text-xs">
                    <tbody>
                      {applyResult.failed_samples.map((s, i) => (
                        <tr key={s.id || i} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono">{s.invoice_number}</td>
                          <td className="px-2 py-1">{s.error}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </details>
            )}

            <p className="text-[10px] text-muted-foreground">
              Started: {applyResult.started_at} | Finished: {applyResult.finished_at}
            </p>
          </div>
        )}

        {/* ── Check-failed breakdown: read-only, entity_id + resolved credential ── */}
        <div className="border-t pt-4 space-y-3" data-testid="pennylane-check-failed-breakdown-section">
          <div>
            <p className="text-sm font-medium">Check-Failed Breakdown</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              For every check-failed row (target: {target}): the stored entity_id, the
              pennylane_company_id that entity_id's own credential currently resolves to (or
              "no credential" if none exists for it at all -- db.invoices never stores a
              Pennylane account/company id directly), invoice ref, date, and counterparty.
              Grouped by (entity_id, pennylane_company_id) so a wrong-entity-on-the-doc pattern
              or a mis-keyed credential is visible at a glance. Read-only -- no writes, no
              reattribution.
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={runBreakdown}
              disabled={breakdownDispatching || breakdownPolling}
              size="sm"
              variant="outline"
              data-testid="pennylane-check-failed-breakdown-run"
            >
              {breakdownPolling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
              Load Breakdown
            </Button>
            {breakdownPolling && (
              <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-1">
                <RefreshCw className="h-3 w-3 animate-spin" />
                {breakdownElapsed}s
              </span>
            )}
          </div>

          {breakdownFailed && (
            <div className="border-2 rounded-lg p-3 border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/20 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" />
              <p className="text-xs text-red-600">{breakdownFailed}</p>
            </div>
          )}

          {breakdown && (
            <div className="space-y-3">
              <Badge className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                {fmtInt(breakdown.total_check_failed)} check-failed row(s) in {breakdown.groups?.length ?? 0} group(s)
              </Badge>

              {breakdown.groups && breakdown.groups.length > 0 ? (
                <div className="space-y-2">
                  {breakdown.groups.map((g, i) => (
                    <details key={`${g.entity_id}-${g.pennylane_company_id}-${i}`} className="border rounded-lg p-2">
                      <summary className="text-sm font-medium cursor-pointer flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs">entity_id: {g.entity_id ?? '(none)'}</span>
                        <span className="font-mono text-xs">
                          pennylane_company_id: {g.pennylane_company_id ?? 'no credential'}
                        </span>
                        {!g.credential_exists_for_entity && (
                          <span className="text-[10px] text-amber-700 dark:text-amber-400">no credential document for this entity_id</span>
                        )}
                        {g.credential_exists_for_entity && g.credential_active === false && (
                          <span className="text-[10px] text-amber-700 dark:text-amber-400">credential exists but inactive</span>
                        )}
                        <Badge variant="outline">{fmtInt(g.count)} row(s)</Badge>
                      </summary>
                      <div className="mt-2 overflow-x-auto border rounded-lg max-h-64 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b bg-muted/30">
                              <th className="px-2 py-1 text-left">Invoice ref</th>
                              <th className="px-2 py-1 text-left">Date</th>
                              <th className="px-2 py-1 text-left">Counterparty</th>
                              <th className="px-2 py-1 text-left">Reason</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.rows.map((r, ri) => (
                              <tr key={r.invoice_id || ri} className="border-b last:border-0">
                                <td className="px-2 py-1 font-mono whitespace-nowrap">{r.invoice_number || r.invoice_id}</td>
                                <td className="px-2 py-1 whitespace-nowrap">{r.issue_date ?? '-'}</td>
                                <td className="px-2 py-1">{r.counterparty ?? '-'}</td>
                                <td className="px-2 py-1 text-muted-foreground">{r.reason}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No check-failed rows for target: {breakdown.target}.</p>
              )}

              <p className="text-[10px] text-muted-foreground">Generated: {breakdown.generated_at}</p>
            </div>
          )}
        </div>

        {/* ── Ownership probe: cross-checks every active credential for check-failed rows ── */}
        <div className="border-t pt-4 space-y-3" data-testid="pennylane-ownership-probe-section">
          <div>
            <p className="text-sm font-medium">Ownership Probe</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              For every check-failed row (target: {target}): asks EVERY active Pennylane
              credential's own API (customer_invoices and supplier_invoices detail endpoints,
              regardless of the row's stored type) whether it recognizes the invoice, and
              confirms any hit against the row's own invoice_number/date/amount before counting
              it as ownership -- a bare HTTP 200 with nothing matching is a coincidental id
              collision (Pennylane ids are per-company), not evidence, and stays visible as an
              unconfirmed hit rather than being treated as a match. Also echoes the stored doc's
              provenance (source, synced_at, created_at, updated_at) so you can see how the
              current entity_id got stamped. Grouped by owning credential. Read-only -- no
              writes, no reattribution.
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={runProbe}
              disabled={probeDispatching || probePolling}
              size="sm"
              variant="outline"
              data-testid="pennylane-ownership-probe-run"
            >
              {probePolling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
              Run Ownership Probe
            </Button>
            {probePolling && (
              <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-1">
                <RefreshCw className="h-3 w-3 animate-spin" />
                {probeElapsed}s
                {probeProgress && ` · ${fmtInt(probeProgress.rows_done)} of ${fmtInt(probeProgress.rows_total)} rows`}
              </span>
            )}
          </div>

          {probeFailed && (
            <div className="border-2 rounded-lg p-3 border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/20 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" />
              <p className="text-xs text-red-600">{probeFailed}</p>
            </div>
          )}

          {probe && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                  {fmtInt(probe.total_check_failed_rows_probed)} row(s) checked against{' '}
                  {fmtInt(probe.active_credentials_probed?.length)} active credential(s)
                </Badge>
                {probe.summary?.single_owner_across_all_rows && (
                  <Badge className="text-[10px] bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                    Single confirmed owner: {probe.summary.single_owner_across_all_rows}
                  </Badge>
                )}
                {probe.summary?.rows_with_no_owner_found > 0 && (
                  <Badge variant="outline" className="text-[10px]">
                    {fmtInt(probe.summary.rows_with_no_owner_found)} row(s) with no owner found
                  </Badge>
                )}
                {probe.summary?.rows_with_ambiguous_owner > 0 && (
                  <Badge className="text-[10px] bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300">
                    {fmtInt(probe.summary.rows_with_ambiguous_owner)} row(s) with ambiguous owner
                  </Badge>
                )}
              </div>

              {probe.active_credentials_probed && probe.active_credentials_probed.length > 0 && (
                <p className="text-[11px] text-muted-foreground">
                  Probed: {probe.active_credentials_probed.map((c) => c.canonical_name || c.entity_id).join(', ')}
                </p>
              )}

              {probe.credential_errors && Object.keys(probe.credential_errors).length > 0 && (
                <div className="border-2 rounded-lg p-2 border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-950/20 flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
                      Some credentials could not be fully checked -- treat "no owner found" for
                      rows they were probed against as unconfirmed, not ruled out.
                    </p>
                    {Object.entries(probe.credential_errors).map(([entityId, messages]) => (
                      <p key={entityId} className="text-[11px] text-amber-700 dark:text-amber-400 font-mono">
                        {entityId}: {messages.join('; ')}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {probe.groups && probe.groups.length > 0 ? (
                <div className="space-y-2">
                  {probe.groups.map((g, i) => (
                    <details key={`${g.owner_entity_id ?? 'none'}-${i}`} className="border rounded-lg p-2">
                      <summary className="text-sm font-medium cursor-pointer flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-xs">
                          owner: {g.owner_canonical_name || g.owner_entity_id || (g.ambiguous ? 'ambiguous (multiple confirmed)' : 'none found')}
                        </span>
                        <Badge variant="outline">{fmtInt(g.count)} row(s)</Badge>
                      </summary>
                      <div className="mt-2 overflow-x-auto border rounded-lg max-h-64 overflow-y-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b bg-muted/30">
                              <th className="px-2 py-1 text-left">Invoice ref</th>
                              <th className="px-2 py-1 text-left">Stored entity_id</th>
                              <th className="px-2 py-1 text-left">Date / amount</th>
                              <th className="px-2 py-1 text-left">Match</th>
                              <th className="px-2 py-1 text-left">Provenance</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.rows.map((r, ri) => (
                              <tr key={r.invoice_id || ri} className="border-b last:border-0 align-top">
                                <td className="px-2 py-1 font-mono whitespace-nowrap">{r.stored_invoice_number || r.invoice_id}</td>
                                <td className="px-2 py-1 font-mono whitespace-nowrap">{r.stored_entity_id ?? '-'}</td>
                                <td className="px-2 py-1 whitespace-nowrap">
                                  {r.stored_issue_date ?? '-'} / {r.stored_amount ?? '-'} {r.stored_currency ?? ''}
                                </td>
                                <td className="px-2 py-1">
                                  {r.confirmed_hits?.length > 0 ? (
                                    r.confirmed_hits.map((h, hi) => (
                                      <div key={hi} className="text-green-700 dark:text-green-400">
                                        {h.credential_entity_id} ({h.endpoint}): ref{h.invoice_number_matches ? '✓' : '✗'} date{h.date_matches ? '✓' : '✗'} amt{h.amount_matches ? '✓' : '✗'}
                                      </div>
                                    ))
                                  ) : r.unconfirmed_hits?.length > 0 ? (
                                    <span className="text-amber-700 dark:text-amber-400">
                                      {r.unconfirmed_hits.length} unconfirmed hit(s) (id collision, fields didn't match)
                                    </span>
                                  ) : (
                                    <span className="text-muted-foreground">no credential recognized this id</span>
                                  )}
                                </td>
                                <td className="px-2 py-1 text-muted-foreground whitespace-nowrap">
                                  synced {r.provenance?.synced_at ?? '-'}<br />
                                  created {r.provenance?.created_at ?? '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </details>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">No check-failed rows for target: {probe.target}.</p>
              )}

              <p className="text-[10px] text-muted-foreground">Generated: {probe.generated_at}</p>
            </div>
          )}
        </div>

        {/* ── Reattribute confirmed rows: preview-first, writes on Apply ── */}
        <div className="border-t pt-4 space-y-3" data-testid="pennylane-reattribution-section">
          <div>
            <p className="text-sm font-medium">Reattribute Confirmed Rows</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Preview lists every check-failed row (target: {target}) whose Ownership Probe
              result is a confirmed single owner different from its stored entity_id -- selected
              by the probe's own confirmation criteria, never a literal entity_id match, so this
              generalises to any future mis-stamped group. Rows that are ambiguous, have no
              confirmed owner, or whose owner isn't in the canonical registry are excluded and
              listed separately with a reason. Apply re-confirms every row fresh (a stale preview
              never authorises a write), sets entity_id to the confirmed owner via the canonical
              registry, and stamps reattributed_from/reattributed_at/reattribution_basis plus
              intercompany_candidate (the original entity_id, in case it reflects a real
              intercompany interest).
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <Button
              onClick={runReattachPreview}
              disabled={reattachDispatching || reattachPreviewPolling || reattachApplyPolling}
              size="sm"
              variant="outline"
              data-testid="pennylane-reattribution-preview-run"
            >
              {reattachPreviewPolling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
              Preview Reattribution
            </Button>
            <Button
              onClick={runReattachApply}
              disabled={reattachDispatching || reattachPreviewPolling || reattachApplyPolling}
              size="sm"
              variant={reattachPreview && reattachPreview.reattributable_count > 0 ? 'default' : 'outline'}
              data-testid="pennylane-reattribution-apply-run"
            >
              {reattachApplyPolling ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
              {reattachPreview ? `Apply (${fmtInt(reattachPreview.reattributable_count)} confirmed)` : 'Apply'}
            </Button>
            {(reattachPreviewPolling || reattachApplyPolling) && (
              <span className="text-xs text-muted-foreground tabular-nums flex items-center gap-1">
                <RefreshCw className="h-3 w-3 animate-spin" />
                {reattachElapsed}s
                {reattachApplyProgress && (
                  reattachApplyProgress.rows_total != null
                    ? ` · confirming ${fmtInt(reattachApplyProgress.rows_done)} of ${fmtInt(reattachApplyProgress.rows_total)} rows`
                    : ` · writing ${fmtInt(reattachApplyProgress.processed)} of ${fmtInt(reattachApplyProgress.total)}`
                )}
              </span>
            )}
          </div>

          {reattachFailed && (
            <div className="border-2 rounded-lg p-3 border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/20 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" />
              <p className="text-xs text-red-600">{reattachFailed}</p>
            </div>
          )}

          {reattachPreview && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className="text-[10px] bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                  {fmtInt(reattachPreview.reattributable_count)} row(s) reattributable
                </Badge>
                <Badge variant="outline" className="text-[10px]">
                  {fmtInt(reattachPreview.skipped_count)} row(s) skipped
                </Badge>
              </div>

              {reattachPreview.reattributable_rows && reattachPreview.reattributable_rows.length > 0 && (
                <div className="overflow-x-auto border rounded-lg max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="px-2 py-1 text-left">Invoice ref</th>
                        <th className="px-2 py-1 text-left">Date / amount</th>
                        <th className="px-2 py-1 text-left">Current entity_id</th>
                        <th className="px-2 py-1 text-left">Target entity_id</th>
                        <th className="px-2 py-1 text-left">Match basis</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reattachPreview.reattributable_rows.map((r, i) => (
                        <tr key={r.invoice_id || i} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono whitespace-nowrap">{r.invoice_number || r.invoice_id}</td>
                          <td className="px-2 py-1 whitespace-nowrap">{r.issue_date ?? '-'} / {r.amount ?? '-'} {r.currency ?? ''}</td>
                          <td className="px-2 py-1 font-mono">{r.current_entity_id}</td>
                          <td className="px-2 py-1 font-mono text-green-700 dark:text-green-400">{r.target_entity_id}</td>
                          <td className="px-2 py-1">{r.match_basis}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {reattachPreview.skipped_rows && reattachPreview.skipped_rows.length > 0 && (
                <details>
                  <summary className="text-sm font-medium cursor-pointer text-muted-foreground">
                    Skipped rows ({reattachPreview.skipped_rows.length})
                  </summary>
                  <div className="mt-2 overflow-x-auto border rounded-lg max-h-48 overflow-y-auto">
                    <table className="w-full text-xs">
                      <tbody>
                        {reattachPreview.skipped_rows.map((r, i) => (
                          <tr key={r.invoice_id || i} className="border-b last:border-0">
                            <td className="px-2 py-1 font-mono whitespace-nowrap">{r.invoice_number || r.invoice_id}</td>
                            <td className="px-2 py-1 font-mono whitespace-nowrap">{r.current_entity_id}</td>
                            <td className="px-2 py-1 text-muted-foreground">{r.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}

              <p className="text-[10px] text-muted-foreground">Generated: {reattachPreview.generated_at}</p>
            </div>
          )}

          {reattachApplyResult && (
            <div className="space-y-2">
              <Badge className="text-[10px] bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300">
                APPLIED -- {fmtInt(reattachApplyResult.reattributed_count)} reattributed,{' '}
                {fmtInt(reattachApplyResult.skipped_count)} skipped
              </Badge>
              {reattachApplyResult.errors && reattachApplyResult.errors.length > 0 && (
                <details>
                  <summary className="text-sm font-medium cursor-pointer text-red-600">
                    Errors ({reattachApplyResult.errors.length})
                  </summary>
                  <div className="mt-2 overflow-x-auto border rounded-lg max-h-48 overflow-y-auto">
                    <table className="w-full text-xs">
                      <tbody>
                        {reattachApplyResult.errors.map((e, i) => (
                          <tr key={e.invoice_id || i} className="border-b last:border-0">
                            <td className="px-2 py-1 font-mono">{e.invoice_id}</td>
                            <td className="px-2 py-1">{e.error}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </details>
              )}
              <p className="text-[10px] text-muted-foreground">
                Started: {reattachApplyResult.started_at} | Finished: {reattachApplyResult.finished_at}
              </p>
            </div>
          )}
        </div>

        {/* ── Intercompany candidates: read-only tag lookup ── */}
        <div className="border-t pt-4 space-y-3" data-testid="pennylane-intercompany-candidates-section">
          <div>
            <p className="text-sm font-medium">Intercompany Candidates</p>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Invoices a Reattribution Apply has tagged intercompany_candidate -- the original
              (now-corrected) entity_id, kept findable in case it reflects a real intercompany
              economic interest that needs the fiscaliste's review. Read-only, no live Pennylane
              calls.
            </p>
          </div>

          <Button
            onClick={runIntercompanyCandidates}
            disabled={icDispatching}
            size="sm"
            variant="outline"
            data-testid="pennylane-intercompany-candidates-run"
          >
            {icDispatching ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Eye className="h-4 w-4 mr-2" />}
            Load Intercompany Candidates
          </Button>

          {icFailed && (
            <div className="border-2 rounded-lg p-3 border-red-300 dark:border-red-700 bg-red-50 dark:bg-red-950/20 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-red-600 flex-shrink-0" />
              <p className="text-xs text-red-600">{icFailed}</p>
            </div>
          )}

          {icResult && (
            <div className="space-y-2">
              <Badge className="text-[10px] bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
                {fmtInt(icResult.count)} intercompany candidate(s)
              </Badge>
              {icResult.rows && icResult.rows.length > 0 && (
                <div className="overflow-x-auto border rounded-lg max-h-64 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="px-2 py-1 text-left">Invoice ref</th>
                        <th className="px-2 py-1 text-left">Current entity_id</th>
                        <th className="px-2 py-1 text-left">Intercompany candidate</th>
                        <th className="px-2 py-1 text-left">Reattributed at</th>
                      </tr>
                    </thead>
                    <tbody>
                      {icResult.rows.map((r, i) => (
                        <tr key={r.id || i} className="border-b last:border-0">
                          <td className="px-2 py-1 font-mono whitespace-nowrap">{r.invoice_number || r.id}</td>
                          <td className="px-2 py-1 font-mono">{r.entity_id}</td>
                          <td className="px-2 py-1 font-mono">{r.intercompany_candidate}</td>
                          <td className="px-2 py-1 text-muted-foreground whitespace-nowrap">{r.reattributed_at ?? '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-[10px] text-muted-foreground">Generated: {icResult.generated_at}</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
